import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "best-price-calendar-container__title" }
const _hoisted_2 = { class: "collapse-body" }
const _hoisted_3 = {
  key: 2,
  class: "best-price-calendar-container__no-offers"
}

import { requestCancelable } from '@components/common/services/request';
import type { BestPackageOfferForDepartureDateResponse } from '@interfaces/api/v1-best-package-offer-for-departure-date';
import type {
	BestAccommodationOfferForCheckInDate,
	BestAccommodationOfferForCheckInDateResponse,
} from '@interfaces/api/v1-best-accommodation-offer-for-check-in-date';
import { scrollIt, logErrorResponse } from '@utils/utils';
import { now, dateDiff } from '@/js/utils/dateTimeUtils';
import { EventBus } from '@global-js/event-bus';
import BestPriceCalendar from '@lmt-rpb/BestPriceCalendar/BestPriceCalendar.vue';
import BestPriceSelection from '@lmt-rpb/BestPriceSelection/BestPriceSelection.vue';
import { useStore } from '@/components/common/store';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { SearchFormDataType } from '@/interfaces/search-form';
import Collapse from '@lmt-rpb/Collapse/Collapse.vue';
import BaseIcon from '@lmt-rpb/BaseIcon/BaseIcon.vue';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import type { MonthData } from '@helper/types/commonProps';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import SelectContentAnalytics from '@modules/analytics/SelectContentAnalytics';
import { API_URLS } from '@global-js/constants';


export default /*@__PURE__*/_defineComponent({
  __name: 'BestPriceCalendarContainer',
  setup(__props) {

const store = useStore();

const bestPriceCalendar = ref<InstanceType<typeof BestPriceCalendar>>();

const offers = ref<BestAccommodationOfferForCheckInDate[]>([]);

const loadedOffersFrom = ref<Date>();

const loadedOffersTo = ref<Date>();

const nowDate = ref<Date>(now() as Date);

const loading = ref(false);

const beforeInitialFetch = ref(true);

const formData = computed((): SearchFormDataType => store.state.searchMask);

const apiData = store.getters['searchMask/apiData'];

const savedFormData = ref({ ...formData.value }); // Copy of formData

const fromDate = computed(() => new Date(formData.value.offerDuration.from as number));

const toDate = computed((): Date => new Date(formData.value.offerDuration.to as number));

const offerType = computed((): 'package' | 'hotel' => (formData.value.onlyHotel ? 'hotel' : 'package'));

const travelDuration = computed((): number => formData.value.travelDuration?.[0] || 7);

const filter = computed({
	get() {
		return formData.value.filter;
	},

	set(date: string | undefined | null) {
		store.commit('searchMask/updateFormData', {
			filter: date,
		});
	},
});

const { isDesktop } = storeToRefs(useBreakpointStore());

const getDateRange = (startDate: Date, rangeLength: number): { from: Date; to: Date } => {
	const from = dateDiff(startDate, nowDate.value) < 0 ? startDate : nowDate.value;
	const to = new Date(startDate.getFullYear(), startDate.getMonth() + rangeLength, travelDuration.value);
	return { from, to };
};

const initialOfferInterval = computed((): { from: Date; to: Date } => {
	const firstDay = new Date(fromDate.value.getFullYear(), fromDate.value.getMonth() - 1, 1);
	const monthsToLoad = Math.min(Math.max(dateDiff(firstDay, toDate.value, 'month'), 4), 6);
	return getDateRange(firstDay, monthsToLoad);
});

const onSelect = (date: string) => {
	setTimeout(() => {
		scrollIt(document.querySelector('.offers__list') as HTMLElement, 1000, 'easeOutQuad', isDesktop.value ? 400 : 200);
	}, 250);
	if (filter.value === date) {
		return;
	}

	// update filter
	filter.value = date;
	EventBus.$emit('SearchMask:MobileSubmit');
};

const onClearSelection = () => {
	bestPriceCalendar.value?.clearSelection();
	// remove filter
	filter.value = '';
	EventBus.$emit('SearchMask:MobileSubmit');
};

const formDataWasChanged = (): boolean => {
	// Excludes for offerlist update
	/* eslint-disable @typescript-eslint/no-unused-vars */
	const {
		destination: currentDestination,
		searchTerm: currentSearchTerm,
		modified: currentModified,
		filter: currentFilter,
		...newFormData
	} = formData.value;

	const {
		destination: savedDestination,
		searchTerm: savedtSearchTerm,
		modified: savedModified,
		filter: savedFilter,
		...newSavedFormData
	} = savedFormData.value;
	/* eslint-enable @typescript-eslint/no-unused-vars */

	// Update savedFormData
	savedFormData.value = { ...formData.value };

	// Check if wanted fields updated
	if (JSON.stringify(newFormData) !== JSON.stringify(newSavedFormData)) {
		return true;
	}

	return false;
};

const updateLoadedDateRange = (start: Date, end: Date) => {
	loadedOffersFrom.value =
		loadedOffersFrom.value === undefined || dateDiff(start, loadedOffersFrom.value) > 0 ? start : loadedOffersFrom.value;
	loadedOffersTo.value = loadedOffersTo.value === undefined || dateDiff(end, loadedOffersTo.value) < 0 ? end : loadedOffersTo.value;
};

const getRequestData = (start: Date, end: Date) => {
	const exclude = offerType.value === 'hotel' ? ['DepartureAirport', 'DirectFlight', 'DepartureTime', 'ArrivalTime'] : [];
	// Removing params which are at the moment specific to the hotellist request
	exclude.push(
		'Regions',
		'Cities',
		'MaxPrice',
		'SortOrder',
		'HotelCategory',
		'MinMeanRecommendationRate',
		'RatingAttributes',
		'HotelAttributes',
		'Countries'
	);
	const body = apiData(
		{
			offerDuration: {
				from: start.getTime(),
				to: end.getTime(),
			},
		},
		exclude
	);
	return body;
};

const fetchOffers = async (start: Date = initialOfferInterval.value.from, end: Date = initialOfferInterval.value.to) => {
	const url = API_URLS.OFFER_CALENDAR[formData.value.onlyHotel ? 'HOTEL' : 'PACKAGE'];
	const requestDate = getRequestData(start, end);
	updateLoadedDateRange(start, end);
	loading.value = true;
	try {
		const resp = await requestCancelable<BestAccommodationOfferForCheckInDateResponse | BestPackageOfferForDepartureDateResponse>(
			{ url, data: requestDate },
			'bestPriceBox'
		);
		if (!resp || !resp.Offers) {
			return;
		}
		offers.value.push(...resp.Offers);
	} catch (error) {
		logErrorResponse('Best Accommodation Offer For Check In Date Response:', error, requestDate, offers.value);
	} finally {
		loading.value = false;
		beforeInitialFetch.value = false;
	}
};

const handleMobileSubmit = () => {
	if (!isDesktop.value && formDataWasChanged()) {
		const { from, to } = initialOfferInterval.value;
		offers.value = [];
		fetchOffers(from, to);
	}
};

const addMonthRight = (monthData: MonthData) => {
	const newMonthStart = new Date(monthData.year, monthData.month + 1, 1);
	const { from, to } = getDateRange(newMonthStart, 1);
	if (loadedOffersTo.value && dateDiff(loadedOffersTo.value, to) > 0) {
		fetchOffers(from, to);
	}
};

const addMonthLeft = (monthData: MonthData) => {
	const newMonth = new Date(monthData.year, monthData.month, 1);
	if (loadedOffersFrom.value && dateDiff(newMonth, loadedOffersFrom.value) > 0) {
		const { from, to } = getDateRange(newMonth, 1);
		fetchOffers(from, to);
	}
};

const changeMonth = (monthData: MonthData) => {
	addMonthRight(monthData);
	addMonthLeft(monthData);
};

const handleCollapseToggle = (isOpen: boolean) => {
	if (isOpen) {
		SelectContentAnalytics.selectContent({
			cId: 'best_price_calendar',
			cType: 'ux_hotelpage_view',
		});
	}
};

onMounted(() => {
	EventBus.$on('SearchMask:MobileSubmit', handleMobileSubmit);
});

onBeforeUnmount(() => {
	EventBus.$off('SearchMask:MobileSubmit', handleMobileSubmit);
});

watch(
	() => formData.value,
	() => {
		if (isDesktop.value && formDataWasChanged()) {
			offers.value = [];
			const { from, to } = initialOfferInterval.value;
			loadedOffersFrom.value = from;
			loadedOffersTo.value = to;
			fetchOffers(from, to);
		}
	},
	{ deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "best-price-calendar-container",
    onClickOnce: _cache[0] || (_cache[0] = ($event: any) => (fetchOffers()))
  }, [
    _createVNode(Collapse, {
      class: "best-price-calendar-container__collapse",
      "keep-alive": true,
      "onCollapse:Toggle": handleCollapseToggle
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", null, [
          _createElementVNode("h2", _hoisted_1, [
            _createVNode(BaseIcon, {
              name: "calendar",
              width: "22",
              height: "24"
            }),
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "best-price-calendar-container__title-text" }, "Sparkalender", -1 /* HOISTED */))
          ]),
          _cache[2] || (_cache[2] = _createElementVNode("div", { class: "best-price-calendar-container__subtitle" }, "Finden Sie günstigere Angebote mit flexiblem Abreisedatum", -1 /* HOISTED */))
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", null, [
            (beforeInitialFetch.value)
              ? (_openBlock(), _createBlock(Loading, {
                  key: 0,
                  "unique-key": "calendar-loader",
                  class: "best-price-calendar-container__loading-icon"
                }))
              : (offers.value.length)
                ? (_openBlock(), _createBlock(BestPriceCalendar, {
                    key: 1,
                    ref_key: "bestPriceCalendar",
                    ref: bestPriceCalendar,
                    offers: offers.value,
                    "start-date": fromDate.value,
                    loading: loading.value,
                    onChangeMonth: changeMonth,
                    onSelectDay: onSelect
                  }, null, 8 /* PROPS */, ["offers", "start-date", "loading"]))
                : (_openBlock(), _createElementBlock("div", _hoisted_3, " Mit den eingegebenen Sucheinstellungen hat der Kalendar keine Angebote erhalten. "))
          ])
        ])
      ]),
      _: 1 /* STABLE */
    }),
    _createVNode(BestPriceSelection, {
      filter: filter.value,
      onDeleteFilter: onClearSelection
    }, null, 8 /* PROPS */, ["filter"])
  ], 32 /* NEED_HYDRATION */))
}
}

})